import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { inr, fbizdate } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Crown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useRangeParams, apiQueryString, DrillDownBanner } from "@/lib/drilldown";
import { SearchInput } from "@/components/pawkt/SearchInput";

function petsLabel(m) {
  const names = (m.pet_names || []).filter(Boolean);
  if (!names.length) return `${m.covered_pet_count || 1} ${(m.covered_pet_count || 1) > 1 ? "pets" : "pet"}`;
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

export default function Memberships() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPerm(user, "memberships.edit");
  const [open, setOpen] = useState(false);
  const [sell, setSell] = useState(null);
  const [benefitsFor, setBenefitsFor] = useState(null);
  const [editMem, setEditMem] = useState(null);
  const [delMem, setDelMem] = useState(null);
  const range = useRangeParams();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get("search") || "");

  const { data: plans, isLoading } = useQuery({ queryKey: ["mplans"], queryFn: async () => (await api.get("/membership-plans")).data });
  const { data: active } = useQuery({
    queryKey: ["memberships", search, range.period, range.start, range.end],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (range.period) {
        params.period = range.period;
        if (range.period === "custom") { params.start = range.start; params.end = range.end; }
      }
      return (await api.get(`/memberships${apiQueryString(params)}`)).data;
    },
  });
  const add = useMutation({ mutationFn: (b) => api.post("/membership-plans", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["mplans"] }); setOpen(false); toast.success("Plan created"); }, onError: (e) => toast.error(apiErr(e)) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/membership-plans/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["mplans"] }); toast.success("Deleted"); } });

  return (
    <div>
      <PageHeader title="Memberships" subtitle="Recurring plans that lock in loyal customers">
        <Button onClick={() => setOpen(true)} data-testid="add-mplan-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> New Plan</Button>
      </PageHeader>
      {isLoading ? <Loading /> : (plans?.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {plans.map((p) => (
            <div key={p.id} data-testid={`mplan-${p.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between">
                <Crown className="h-6 w-6 text-amber-500" />
                <button onClick={() => del.mutate(p.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="font-display text-lg font-bold mt-2">{p.name}</div>
              <div className="font-display text-2xl font-bold text-slate-900">{inr(p.price)}<span className="text-sm font-normal text-slate-400">/{p.billing_frequency}</span></div>
              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                <div>{p.discount_pct}% service discount</div>
                <div>{p.loyalty_multiplier}× loyalty points</div>
                <div>{p.included_visits || 0} included visits</div>
              </div>
              <Button size="sm" onClick={() => setSell(p)} data-testid={`sell-mplan-${p.id}`} className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 text-white">Sell to customer</Button>
              <Button size="sm" variant="outline" onClick={() => setBenefitsFor(p)} data-testid={`benefits-mplan-${p.id}`} className="mt-2 w-full">Configure benefits{(p.service_benefits?.length || 0) > 0 ? ` (${p.service_benefits.length})` : ""}</Button>
            </div>
          ))}
        </div>
      ) : <Empty icon={Crown} title="No membership plans" subtitle="Create Basic / Premium / VIP tiers." />)}

      <h3 className="font-display text-lg font-semibold mb-2">Active memberships</h3>
      <div className="mb-3 max-w-md">
        <SearchInput value={search} onChange={setSearch} testid="memberships-search" placeholder="Search by customer name, mobile or plan…" />
      </div>
      <DrillDownBanner metric="membership" count={(active || []).length} />
      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {(active || []).length === 0 && <p className="text-sm text-slate-400 py-6 text-center">None yet</p>}
        {(active || []).map((m) => (
          <div key={m.id} className="flex items-center gap-3 px-4 py-3 text-sm" data-testid={`mem-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate" data-testid={`mem-customer-${m.id}`}>{m.customer_name || "Unknown customer"}</div>
              <div className="text-xs text-slate-500 truncate">{m.plan_name}{m.customer_mobile ? ` · ${m.customer_mobile}` : ""}</div>
            </div>
            <div className="hidden sm:flex flex-col items-end shrink-0 max-w-[160px]" data-testid={`mem-pets-${m.id}`}>
              <span className="text-slate-700 font-medium truncate w-full text-right">{petsLabel(m)}</span>
              <span className="text-[11px] text-slate-400">{inr(m.unit_price ?? m.price)}/pet · {m.covered_pet_count || 1}</span>
            </div>
            <div className="flex flex-col items-end shrink-0" data-testid={`mem-total-${m.id}`}>
              <span className="text-slate-900 font-semibold">{inr(m.total_amount ?? m.price)}</span>
              {Number(m.discount_amount) > 0
                ? <span className="text-[11px] text-emerald-600" data-testid={`mem-discount-${m.id}`}>−{inr(m.discount_amount)} off</span>
                : <span className="text-[11px] text-slate-400">expires {fbizdate(m.expiry_date)}</span>}
            </div>
            <StatusBadge status={m.status} />
            <button onClick={() => setEditMem(m)} data-testid={`mem-edit-${m.id}`} className="text-xs font-medium text-slate-900 hover:underline shrink-0">Edit</button>
            {canManage && (
              <button onClick={() => setDelMem(m)} data-testid={`mem-delete-${m.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700 hover:underline shrink-0"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
            )}
          </div>
        ))}
      </div>

      {delMem && <DeleteMembershipModal m={delMem} onClose={() => setDelMem(null)}
        onDone={() => { qc.invalidateQueries({ queryKey: ["memberships"] }); setDelMem(null); }} />}

      {editMem && <EditMembershipModal m={editMem} onClose={() => setEditMem(null)}
        onDone={() => { qc.invalidateQueries({ queryKey: ["memberships"] }); setEditMem(null); }} />}

      <FormModal open={open} onOpenChange={setOpen} title="New Membership Plan" testid="mplan-modal"
        initial={{ billing_frequency: "monthly", duration_days: 30, loyalty_multiplier: 1, discount_pct: 0 }}
        submitLabel="Create plan" fields={[
          { name: "name", label: "Plan name", full: true },
          { name: "price", label: "Price (₹)", type: "number" },
          { name: "billing_frequency", label: "Billing", type: "select", options: ["monthly", "quarterly", "annual", "one_time"].map((v) => ({ value: v, label: v })) },
          { name: "duration_days", label: "Duration (days)", type: "number" },
          { name: "discount_pct", label: "Service discount %", type: "number" },
          { name: "product_discount_pct", label: "Product discount %", type: "number" },
          { name: "loyalty_multiplier", label: "Loyalty multiplier", type: "number" },
          { name: "included_visits", label: "Included visits", type: "number" },
        ]} onSubmit={(v) => add.mutateAsync(v)} />

      {sell && <SellModal plan={sell} endpoint="/memberships/purchase" backdate showPetQuantity onClose={() => setSell(null)}
        onDone={(viewId) => { qc.invalidateQueries({ queryKey: ["memberships"] }); setSell(null); if (viewId) navigate(`/customers/${sell._customer_id || ""}`); }} />}

      {benefitsFor && <BenefitsModal plan={benefitsFor} onClose={() => setBenefitsFor(null)}
        onDone={() => { qc.invalidateQueries({ queryKey: ["mplans"] }); setBenefitsFor(null); }} />}
    </div>
  );
}

function PetPicker({ customerId, selected, onToggle, testid }) {
  const { data: pets } = useQuery({
    queryKey: ["cust-pets", customerId],
    queryFn: async () => (await api.get(`/customers/${customerId}`)).data,
    enabled: !!customerId,
  });
  const list = pets?.pets || [];
  if (!customerId) return <p className="text-xs text-slate-400">Select a customer first.</p>;
  if (!list.length) return <p className="text-xs text-slate-400">This customer has no pets on file.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {list.map((p) => {
        const on = selected.includes(p.id);
        return (
          <button type="button" key={p.id} data-testid={`${testid}-${p.id}`} onClick={() => onToggle(p.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {on ? "✓ " : ""}{p.name}
          </button>
        );
      })}
    </div>
  );
}

function DiscountFields({ type, value, onType, onValue, disabled, testidPrefix = "disc" }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2">
      <div>
        <Label className="text-xs text-slate-500 mb-1 block">Discount</Label>
        <Select value={type} onValueChange={onType} disabled={disabled}>
          <SelectTrigger className="bg-white" data-testid={`${testidPrefix}-type`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="percentage">Percent %</SelectItem>
            <SelectItem value="fixed">Fixed ₹</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-500 mb-1 block">{type === "percentage" ? "Percent" : "Amount"}</Label>
        <Input type="number" min={0} value={type === "none" ? "" : value} disabled={disabled || type === "none"}
          onChange={(e) => onValue(e.target.value)} data-testid={`${testidPrefix}-value`} className="bg-white" />
      </div>
    </div>
  );
}

function EditMembershipModal({ m, onClose, onDone }) {
  const { user } = useAuth();
  const canFinancial = hasPerm(user, "invoices.edit");
  const d = (s) => (s ? String(s).slice(0, 10) : "");
  const isPaid = Boolean(m.invoice_id); // memberships are billed+paid at sale
  const gross = Number(m.gross_amount ?? m.total_amount ?? m.price) || 0;
  const [v, setV] = useState({
    start_date: d(m.start_date), expiry_date: d(m.expiry_date), sale_date: d(m.sale_date || m.start_date),
    notes: m.notes || "", reason: "",
    discount_type: m.discount_type || "none", discount_value: m.discount_value || 0,
  });
  const [petIds, setPetIds] = useState(m.pet_ids || []);
  const [busy, setBusy] = useState(false);
  const togglePet = (id) => setPetIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const discAmt = v.discount_type === "percentage" ? Math.round(gross * (Number(v.discount_value) || 0)) / 100
    : v.discount_type === "fixed" ? Math.min(gross, Number(v.discount_value) || 0) : 0;
  const finalAmt = Math.max(0, gross - discAmt);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        start_date: v.start_date, expiry_date: v.expiry_date, sale_date: v.sale_date,
        notes: v.notes, pet_ids: petIds, reason: v.reason || undefined,
      };
      if (!isPaid && canFinancial) { payload.discount_type = v.discount_type; payload.discount_value = Number(v.discount_value) || 0; }
      await api.patch(`/memberships/${m.id}`, payload);
      toast.success("Membership updated"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md max-h-[88vh] overflow-y-auto" data-testid="mem-edit-modal">
        <DialogHeader><DialogTitle className="font-display">Edit · {m.plan_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div><b>{m.customer_name}</b> · {m.plan_name}</div>
          </div>
          <div><label className="text-xs font-medium text-slate-600">Sale date (actual purchase date)</label>
            <input type="date" data-testid="mem-edit-sale" value={v.sale_date} max={new Date().toLocaleDateString("en-CA")}
              onChange={(e) => setV((s) => ({ ...s, sale_date: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <p className="text-[11px] text-slate-400 mt-0.5">Moves dashboard membership revenue to this month. Invoice/payment dates are unchanged.</p></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-medium text-slate-600">Start date</label>
              <input type="date" data-testid="mem-edit-start" value={v.start_date} onChange={(e) => setV((s) => ({ ...s, start_date: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-medium text-slate-600">Expiry date</label>
              <input type="date" data-testid="mem-edit-expiry" value={v.expiry_date} onChange={(e) => setV((s) => ({ ...s, expiry_date: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
          </div>
          <div><label className="text-xs font-medium text-slate-600">Covered pets</label>
            <div className="mt-1"><PetPicker customerId={m.customer_id} selected={petIds} onToggle={togglePet} testid="mem-edit-pets" /></div></div>
          <div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Original price</span><span className="tabular">{inr(gross)}</span>
            </div>
            {isPaid ? (
              <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="mem-edit-discount-locked">
                Discount is locked — this membership is already paid. Use the refund / financial-adjustment flow to change the amount.
                {Number(m.discount_amount) > 0 && <div className="mt-0.5">Current discount: {inr(m.discount_amount)}</div>}
              </div>
            ) : (
              <div className="mt-1">
                <DiscountFields type={v.discount_type} value={v.discount_value} disabled={!canFinancial}
                  onType={(t) => setV((s) => ({ ...s, discount_type: t }))} onValue={(x) => setV((s) => ({ ...s, discount_value: x }))}
                  testidPrefix="mem-edit-disc" />
                <div className="mt-1 flex items-center justify-between text-sm font-semibold">
                  <span className="text-slate-500 text-xs">Final amount</span><span className="tabular">{inr(finalAmt)}</span>
                </div>
              </div>
            )}
          </div>
          <div><label className="text-xs font-medium text-slate-600">Reason (audit)</label>
            <input data-testid="mem-edit-reason" value={v.reason} onChange={(e) => setV((s) => ({ ...s, reason: e.target.value }))} placeholder="Why is this being corrected?" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-medium text-slate-600">Notes</label>
            <textarea data-testid="mem-edit-notes" value={v.notes} onChange={(e) => setV((s) => ({ ...s, notes: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="mem-edit-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteMembershipModal({ m, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const pets = (m.pet_names || []).filter(Boolean).join(", ");
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.post(`/memberships/${m.id}/void`, {});
      toast.success("Membership deleted successfully.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="mem-delete-modal">
        <DialogHeader><DialogTitle className="font-display text-rose-700">Delete Membership?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg bg-slate-50 px-3 py-2.5 space-y-1 text-slate-700">
            <div className="flex justify-between gap-3"><span className="text-slate-500">Customer</span><b data-testid="mem-delete-customer">{m.customer_name || "Unknown customer"}</b></div>
            {pets && <div className="flex justify-between gap-3"><span className="text-slate-500">Pet(s)</span><b data-testid="mem-delete-pets">{pets}</b></div>}
            <div className="flex justify-between gap-3"><span className="text-slate-500">Membership</span><b data-testid="mem-delete-plan">{m.plan_name}</b></div>
            <div className="flex justify-between gap-3"><span className="text-slate-500">Sale date</span><b data-testid="mem-delete-sale">{fbizdate(m.sale_date || m.start_date)}</b></div>
            <div className="flex justify-between gap-3"><span className="text-slate-500">Amount paid</span><b data-testid="mem-delete-amount">{inr(m.total_amount ?? m.price)}</b></div>
            <div className="flex justify-between gap-3"><span className="text-slate-500">Current status</span><b data-testid="mem-delete-status" className="capitalize">{m.status}</b></div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-800" data-testid="mem-delete-warning">
            This will deactivate this membership. The membership will no longer be available for active benefits. Historical billing/payment records will not be deleted.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="mem-delete-cancel">Cancel</Button>
          <Button onClick={doDelete} disabled={busy} data-testid="mem-delete-confirm" className="bg-rose-600 hover:bg-rose-700 text-white">{busy ? "Deleting…" : "Delete Membership"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const BENEFIT_TYPES = [
  { value: "percent_discount", label: "% discount" },  { value: "fixed_discount", label: "₹ off per unit" },
  { value: "member_price", label: "₹ member price" },
];

function BenefitsModal({ plan, onClose, onDone }) {
  const [rows, setRows] = useState((plan.service_benefits || []).map((b) => ({ ...b })));
  const [busy, setBusy] = useState(false);
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const add = () => setRows((r) => [...r, { service_id: "", benefit_type: "percent_discount", value: 0 }]);
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const rm = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const save = async () => {
    const clean = rows.filter((r) => r.service_id).map((r) => ({ service_id: r.service_id, benefit_type: r.benefit_type, value: Number(r.value) || 0 }));
    setBusy(true);
    try {
      await api.put(`/membership-plans/${plan.id}`, { ...plan, service_benefits: clean });
      toast.success("Benefits saved"); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-xl" data-testid="benefits-modal">
        <DialogHeader><DialogTitle className="font-display">{plan.name} · Service benefits</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Per-service member pricing. Overrides the flat {plan.discount_pct || 0}% discount for the chosen service. Applied server-side at billing.</p>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {rows.length === 0 && <p className="text-sm text-slate-400 py-3 text-center">No per-service benefits. The flat discount applies to all services.</p>}
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end" data-testid={`benefit-row-${i}`}>
              <div><Label className="text-xs text-slate-500 mb-1 block">Service</Label>
                <Select value={row.service_id} onValueChange={(v) => upd(i, "service_id", v)}>
                  <SelectTrigger className="bg-white" data-testid={`benefit-service-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-56">{(services || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {inr(s.price)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Type</Label>
                <Select value={row.benefit_type} onValueChange={(v) => upd(i, "benefit_type", v)}>
                  <SelectTrigger className="bg-white w-32" data-testid={`benefit-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{BENEFIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Value</Label>
                <Input type="number" value={row.value} onChange={(e) => upd(i, "value", e.target.value)} className="bg-white w-24" data-testid={`benefit-value-${i}`} /></div>
              <button onClick={() => rm(i)} className="text-rose-500 hover:bg-rose-50 rounded p-2 mb-0.5" data-testid={`benefit-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={add} data-testid="benefit-add" className="gap-1"><Plus className="h-4 w-4" /> Add service benefit</Button>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="benefits-save" className="bg-slate-900 text-white">{busy ? "Saving…" : "Save benefits"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SellModal({ plan, endpoint, onClose, onDone, backdate = false, included = "", showPetQuantity = false }) {
  const { user } = useAuth();
  const canBackdate = hasPerm(user, "memberships.backdate");
  const todayStr = () => new Date().toLocaleDateString("en-CA"); // local YYYY-MM-DD
  const [customerId, setCustomerId] = useState("");
  const [method, setMethod] = useState("upi");
  const [startDate, setStartDate] = useState(todayStr());
  const [reason, setReason] = useState("");
  const [petQty, setPetQty] = useState(1);
  const [selectedPets, setSelectedPets] = useState([]);
  const [discType, setDiscType] = useState("none");
  const [discValue, setDiscValue] = useState(0);
  const [busy, setBusy] = useState(false);
  const [overlap, setOverlap] = useState(null);
  const { data: custs } = useQuery({ queryKey: ["customers-lite"], queryFn: async () => (await api.get("/customers?limit=200")).data });
  const { data: allBranches = [] } = useQuery({ queryKey: ["sell-branches"], queryFn: async () => (await api.get("/branches", { headers: { "X-Dashboard-Branch": "" } })).data });
  const sellBranches = user?.branch_ids?.length ? allBranches.filter((b) => user.branch_ids.includes(b.id)) : allBranches;
  const [branchId, setBranchId] = useState("");
  const needBranch = allBranches.length > 1;

  const unitPrice = Number(plan.price) || 0;
  const petCount = selectedPets.length;
  const qty = showPetQuantity ? (petCount > 0 ? petCount : Math.max(1, Number(petQty) || 1)) : 1;
  const grossAmount = unitPrice * qty;
  const discAmount = discType === "percentage" ? Math.round(grossAmount * (Number(discValue) || 0)) / 100
    : discType === "fixed" ? Math.min(grossAmount, Number(discValue) || 0) : 0;
  const totalAmount = Math.max(0, grossAmount - discAmount);
  const togglePet = (id) => setSelectedPets((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const isBackdated = backdate && startDate < todayStr();
  const durationDays = plan.duration_days || 30;
  const expiryStr = (() => {
    if (!backdate || !startDate) return "";
    const d = new Date(startDate + "T00:00:00");
    d.setDate(d.getDate() + durationDays);
    return d.toLocaleDateString("en-CA");
  })();
  const expired = backdate && expiryStr && expiryStr < todayStr();

  const doPost = async (allowOverlap) => {
    const payload = { customer_id: customerId, plan_id: plan.id, payment_method: method, ...(branchId ? { branch_id: branchId } : {}) };
    if (backdate) { payload.start_date = startDate; payload.backdate_reason = reason; payload.allow_overlap = !!allowOverlap; }
    if (showPetQuantity) {
      if (selectedPets.length) payload.pet_ids = selectedPets;
      else payload.pet_quantity = qty;
    }
    if (discType !== "none") { payload.discount_type = discType; payload.discount_value = Number(discValue) || 0; }
    await api.post(endpoint, payload);
  };

  const submit = async (allowOverlap = false) => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (isBackdated && !reason.trim()) { toast.error("A reason is required to backdate"); return; }
    setBusy(true);
    try {
      await doPost(allowOverlap);
      toast.success(`${plan.name} ${isBackdated ? "recorded" : "sold"} · ${inr(totalAmount)}`);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "MEMBERSHIP_OVERLAP") setOverlap(d);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm max-h-[88vh] overflow-y-auto" data-testid="sell-modal">
        <DialogHeader><DialogTitle className="font-display">Sell {plan.name} · {inr(plan.price)}{showPetQuantity ? "/pet" : ""}</DialogTitle></DialogHeader>
        {overlap ? (
          <div className="space-y-3" data-testid="membership-overlap">
            <p className="text-sm text-slate-700">{overlap.message}</p>
            <div className="flex flex-col gap-2">
              <Button variant="outline" onClick={() => { setOverlap(null); onDone(overlap.existing?.id); }} data-testid="overlap-view" className="min-h-[44px]">View existing</Button>
              <Button onClick={() => { setOverlap(null); submit(true); }} disabled={busy} data-testid="overlap-continue" className="min-h-[44px] bg-slate-900 text-white">Continue anyway</Button>
              <Button variant="ghost" onClick={() => setOverlap(null)} className="min-h-[44px]">Cancel</Button>
            </div>
          </div>
        ) : (
        <>
        <div className="space-y-3">
          {included && <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-800" data-testid="sell-included">Customer receives <b>{included}</b></div>}
          <div><Label className="text-xs text-slate-500 mb-1 block">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}><SelectTrigger className="bg-white min-h-[44px]" data-testid="sell-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{(custs?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          {backdate && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Sale date (actual purchase date)</Label>
              <Input type="date" value={startDate} max={todayStr()} disabled={!canBackdate}
                onChange={(e) => setStartDate(e.target.value)} className="bg-white min-h-[44px]" data-testid="sell-start-date" />
              {!canBackdate && <p className="text-[11px] text-slate-400 mt-1">Only owner/admin/manager can set an earlier start date.</p>}
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-slate-500">Expiry (auto)</span>
                <span className="font-semibold text-slate-800" data-testid="sell-expiry">{expiryStr || "—"}{expired ? " · expired" : ""}</span>
              </div>
              {isBackdated && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2" data-testid="backdated-indicator">
                  <p className="text-xs font-semibold text-amber-700">Backdated membership</p>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for backdating"
                    className="bg-white mt-1.5 min-h-[44px]" data-testid="sell-backdate-reason" />
                </div>
              )}
            </div>
          )}
          {needBranch && <div><Label className="text-xs text-slate-500 mb-1 block">Branch where this sale was made</Label>
            <Select value={branchId} onValueChange={setBranchId}><SelectTrigger className="bg-white min-h-[44px]" data-testid="sell-branch"><SelectValue placeholder="Choose branch" /></SelectTrigger>
              <SelectContent className="bg-white">{sellBranches.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`sell-branch-${b.id}`}>{b.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-xs text-slate-500 mb-1 block">Payment</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger className="bg-white min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{["upi", "cash", "card"].map((m) => <SelectItem key={m} value={m} className="uppercase">{m}</SelectItem>)}</SelectContent></Select></div>
          {showPetQuantity && customerId && (
            <div data-testid="sell-pet-select-block">
              <Label className="text-xs text-slate-500 mb-1 block">Covered pets (select the pets this membership covers)</Label>
              <PetPicker customerId={customerId} selected={selectedPets} onToggle={togglePet} testid="sell-pets" />
              <p className="text-[11px] text-slate-400 mt-1">Or leave unselected and set a count below.</p>
            </div>
          )}
          {showPetQuantity && selectedPets.length === 0 && (
            <div data-testid="sell-pet-quantity-block">
              <Label className="text-xs text-slate-500 mb-1 block">Number of pets covered</Label>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPetQty((q) => Math.max(1, (Number(q) || 1) - 1))}
                  data-testid="sell-pet-qty-dec" className="h-10 w-10 rounded-lg border border-slate-200 bg-white text-lg font-bold text-slate-700 hover:bg-slate-50">−</button>
                <Input type="number" min={1} value={petQty} data-testid="sell-pet-qty"
                  onChange={(e) => setPetQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                  className="bg-white w-20 text-center min-h-[44px]" />
                <button type="button" onClick={() => setPetQty((q) => Math.min(100, (Number(q) || 1) + 1))}
                  data-testid="sell-pet-qty-inc" className="h-10 w-10 rounded-lg border border-slate-200 bg-white text-lg font-bold text-slate-700 hover:bg-slate-50">+</button>
              </div>
            </div>
          )}
          <div data-testid="sell-discount-block">
            <DiscountFields type={discType} value={discValue} onType={setDiscType} onValue={setDiscValue} testidPrefix="sell-disc" />
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600 space-y-1">
            <div className="flex items-center justify-between"><span>{qty} × {inr(unitPrice)}</span><span className="tabular" data-testid="sell-gross">{inr(grossAmount)}</span></div>
            {discAmount > 0 && <div className="flex items-center justify-between text-emerald-600"><span>Discount</span><span className="tabular" data-testid="sell-discount">−{inr(discAmount)}</span></div>}
            <div className="flex items-center justify-between font-bold text-slate-900 pt-1 border-t border-slate-200"><span>Final amount</span><span className="tabular" data-testid="sell-total">{inr(totalAmount)}</span></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="min-h-[44px]">Cancel</Button>
          <Button onClick={() => submit(false)} disabled={busy} data-testid="sell-submit" className="min-h-[44px] bg-emerald-600 text-white">{busy ? "Processing…" : `Sell & collect ${inr(totalAmount)}`}</Button></DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
